import React, { useState } from 'react';
import { Action } from '../types/workflow';

interface NestedActionsEditorProps {
    parentAction: Action;
    onUpdate: (updatedAction: Action) => void;
    onClose: () => void;
}

const NestedActionsEditor: React.FC<NestedActionsEditorProps> = ({ parentAction, onUpdate, onClose }) => {
    const [nestedActions, setNestedActions] = useState<Action[]>(parentAction.actions || []);
    const [selectedAction, setSelectedAction] = useState<Action | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [containerConfig, setContainerConfig] = useState<Action>(parentAction);

    const actionTemplates = [
        { type: 'open_browser', label: 'Open Browser' },
        { type: 'close_browser', label: 'Close Browser' },
        { type: 'click', label: 'Click' },
        { type: 'hover', label: 'Hover' },
        { type: 'write', label: 'Write' },
        { type: 'sleep', label: 'Sleep' },
        { type: 'get_element', label: 'Get Element' },
        { type: 'get_elements', label: 'Get Elements' },
        { type: 'api_call', label: 'API Call' },
        { type: 'switch_to_new_window', label: 'Switch Window' },
        { type: 'switch_to_frame', label: 'Switch Frame' },
    ];

    const createNewAction = (type: string): Action => {
        const baseAction: Action = { type: type as any };
        
        switch (type) {
            case 'open_browser':
                return { ...baseAction, url: 'https://' };
            case 'close_browser':
                return baseAction;
            case 'sleep':
                return { ...baseAction, duration: 1 };
            case 'click':
            case 'hover':
            case 'get_element':
            case 'get_elements':
                return { ...baseAction, locator: { method: 'CSS_SELECTOR', value: '' } };
            case 'write':
                return { ...baseAction, locator: { method: 'CSS_SELECTOR', value: '' }, text: '' };
            case 'api_call':
                return { ...baseAction, method: 'POST', url: '', headers: {}, body: {} };
            case 'switch_to_new_window':
                return baseAction;
            case 'switch_to_frame':
                return { ...baseAction, frame_index: 0 };
            default:
                return baseAction;
        }
    };

    const addAction = (type: string) => {
        const newAction = createNewAction(type);
        setNestedActions([...nestedActions, newAction]);
    };

    const removeAction = (index: number) => {
        setNestedActions(nestedActions.filter((_, i) => i !== index));
        if (selectedIndex === index) {
            setSelectedAction(null);
            setSelectedIndex(-1);
        }
    };

    const updateAction = (index: number, action: Action) => {
        const updated = [...nestedActions];
        updated[index] = action;
        setNestedActions(updated);
        setSelectedAction(action);
    };

    const moveUp = (index: number) => {
        if (index > 0) {
            const updated = [...nestedActions];
            [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
            setNestedActions(updated);
        }
    };

    const moveDown = (index: number) => {
        if (index < nestedActions.length - 1) {
            const updated = [...nestedActions];
            [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
            setNestedActions(updated);
        }
    };

    const handleSave = () => {
        const updatedParent = {
            ...containerConfig,
            actions: nestedActions
        };
        onUpdate(updatedParent);
        onClose();
    };

    return (
        <div className="nested-actions-modal">
            <div className="nested-actions-overlay" onClick={onClose}></div>
            <div className="nested-actions-content">
                <div className="nested-actions-header">
                    <h3>Edit {parentAction.type.toUpperCase()} - Configuration & Nested Actions</h3>
                    <button onClick={onClose} className="close-btn">×</button>
                </div>
                
                <div className="nested-actions-body">
                    <div className="nested-actions-list">
                        {/* Container Configuration Section */}
                        <div className="container-config-section">
                            <h4>{parentAction.type.toUpperCase()} Configuration</h4>
                            
                            {containerConfig.type === 'loop' && (
                                <div className="config-form">
                                    <div className="form-group">
                                        <label>Iterate Over:</label>
                                        <input
                                            type="text"
                                            value={containerConfig.iterate_over || ''}
                                            onChange={(e) => setContainerConfig({ ...containerConfig, iterate_over: e.target.value })}
                                            placeholder="Variable to iterate"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Store Current As:</label>
                                        <input
                                            type="text"
                                            value={containerConfig.store_current_as || ''}
                                            onChange={(e) => setContainerConfig({ ...containerConfig, store_current_as: e.target.value })}
                                            placeholder="Current item variable"
                                        />
                                    </div>
                                </div>
                            )}

                            {(containerConfig.type === 'while' || containerConfig.type === 'if') && (
                                <div className="config-form">
                                    <div className="form-group">
                                        <label>Condition Type:</label>
                                        <select
                                            value={containerConfig.condition?.type || 'element_exists'}
                                            onChange={(e) => setContainerConfig({
                                                ...containerConfig,
                                                condition: {
                                                    ...containerConfig.condition!,
                                                    type: e.target.value as any
                                                }
                                            })}
                                        >
                                            <option value="element_exists">Element Exists</option>
                                            <option value="element_not_exists">Element Not Exists</option>
                                            <option value="text_equals">Text Equals</option>
                                            <option value="text_contains">Text Contains</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Locator Method:</label>
                                        <select
                                            value={containerConfig.condition?.locator?.method || 'CSS_SELECTOR'}
                                            onChange={(e) => setContainerConfig({
                                                ...containerConfig,
                                                condition: {
                                                    ...containerConfig.condition!,
                                                    locator: {
                                                        ...containerConfig.condition?.locator!,
                                                        method: e.target.value as any
                                                    }
                                                }
                                            })}
                                        >
                                            <option value="CSS_SELECTOR">CSS Selector</option>
                                            <option value="XPATH">XPath</option>
                                            <option value="ID">ID</option>
                                            <option value="NAME">Name</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Locator Value:</label>
                                        <input
                                            type="text"
                                            value={containerConfig.condition?.locator?.value || ''}
                                            onChange={(e) => setContainerConfig({
                                                ...containerConfig,
                                                condition: {
                                                    ...containerConfig.condition!,
                                                    locator: {
                                                        ...containerConfig.condition?.locator!,
                                                        value: e.target.value
                                                    }
                                                }
                                            })}
                                            placeholder="Enter selector"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <h4>Nested Actions</h4>
                        <div className="action-templates">
                            {actionTemplates.map(template => (
                                <button
                                    key={template.type}
                                    onClick={() => addAction(template.type)}
                                    className="add-action-btn"
                                >
                                    + {template.label}
                                </button>
                            ))}
                        </div>
                        
                        <div className="actions-list-container">
                            {nestedActions.length === 0 ? (
                                <p className="empty-message">No actions yet. Add actions using the buttons above.</p>
                            ) : (
                                <ul className="actions-list">
                                    {nestedActions.map((action, index) => (
                                        <li
                                            key={index}
                                            className={`action-list-item ${selectedIndex === index ? 'selected' : ''}`}
                                            onClick={() => {
                                                setSelectedAction(action);
                                                setSelectedIndex(index);
                                            }}
                                        >
                                            <span className="action-number">{index + 1}.</span>
                                            <span className="action-type">{action.type}</span>
                                            <div className="action-controls">
                                                <button onClick={(e) => { e.stopPropagation(); moveUp(index); }} disabled={index === 0}>↑</button>
                                                <button onClick={(e) => { e.stopPropagation(); moveDown(index); }} disabled={index === nestedActions.length - 1}>↓</button>
                                                <button onClick={(e) => { e.stopPropagation(); removeAction(index); }} className="delete-btn">×</button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    
                    {selectedAction && selectedIndex >= 0 && (
                        <div className="action-editor">
                            <h4>Edit Action {selectedIndex + 1}: {selectedAction.type}</h4>
                            <ActionForm
                                action={selectedAction}
                                onChange={(updated) => updateAction(selectedIndex, updated)}
                            />
                        </div>
                    )}
                </div>
                
                <div className="nested-actions-footer">
                    <button onClick={handleSave} className="btn-save">Save All Actions</button>
                    <button onClick={onClose} className="btn-cancel">Cancel</button>
                </div>
            </div>
        </div>
    );
};

// Reusable form component for editing actions
const ActionForm: React.FC<{ action: Action; onChange: (action: Action) => void }> = ({ action, onChange }) => {
    return (
        <div className="action-form">
            {action.type === 'open_browser' && (
                <div className="form-group">
                    <label>URL:</label>
                    <input
                        type="text"
                        value={action.url || ''}
                        onChange={(e) => onChange({ ...action, url: e.target.value })}
                        placeholder="https://example.com"
                    />
                </div>
            )}

            {action.type === 'close_browser' && (
                <div className="form-group">
                    <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                        This action closes the browser. No configuration needed.
                    </p>
                </div>
            )}

            {(action.type === 'click' || action.type === 'hover' || action.type === 'write' || action.type === 'get_element' || action.type === 'get_elements') && (
                <>
                    <div className="form-group">
                        <label>Locator Method:</label>
                        <select
                            value={action.locator?.method || 'CSS_SELECTOR'}
                            onChange={(e) => onChange({ ...action, locator: { ...action.locator!, method: e.target.value as any } })}
                        >
                            <option value="CSS_SELECTOR">CSS Selector</option>
                            <option value="XPATH">XPath</option>
                            <option value="ID">ID</option>
                            <option value="NAME">Name</option>
                            <option value="CLASS_NAME">Class Name</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Locator Value:</label>
                        <input
                            type="text"
                            value={action.locator?.value || ''}
                            onChange={(e) => onChange({ ...action, locator: { ...action.locator!, value: e.target.value } })}
                        />
                    </div>
                </>
            )}

            {(action.type === 'click' || action.type === 'hover' || action.type === 'write' || action.type === 'get_element' || action.type === 'get_elements') && (
                <div className="form-group">
                    <label>Parent Element (optional):</label>
                    <input
                        type="text"
                        value={action.parent || ''}
                        onChange={(e) => onChange({ ...action, parent: e.target.value })}
                        placeholder="Parent variable"
                    />
                </div>
            )}
            
            {action.type === 'write' && (
                <div className="form-group">
                    <label>Text:</label>
                    <input
                        type="text"
                        value={action.text || ''}
                        onChange={(e) => onChange({ ...action, text: e.target.value })}
                    />
                </div>
            )}
            
            {action.type === 'sleep' && (
                <div className="form-group">
                    <label>Duration (seconds):</label>
                    <input
                        type="number"
                        value={action.duration || 0}
                        onChange={(e) => onChange({ ...action, duration: parseInt(e.target.value) })}
                    />
                </div>
            )}
            
            {(action.type === 'get_element' || action.type === 'get_elements') && (
                <>
                    <div className="form-group">
                        <label>Store As:</label>
                        <input
                            type="text"
                            value={action.store_as || ''}
                            onChange={(e) => onChange({ ...action, store_as: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Regex (optional):</label>
                        <input
                            type="text"
                            value={action.regex || ''}
                            onChange={(e) => onChange({ ...action, regex: e.target.value })}
                            placeholder="Regex pattern"
                        />
                    </div>
                </>
            )}
            
            {action.type === 'api_call' && (
                <>
                    <div className="form-group">
                        <label>Method:</label>
                        <select
                            value={action.method || 'POST'}
                            onChange={(e) => onChange({ ...action, method: e.target.value })}
                        >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                            <option value="DELETE">DELETE</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>URL:</label>
                        <input
                            type="text"
                            value={action.url || ''}
                            onChange={(e) => onChange({ ...action, url: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Headers (JSON):</label>
                        <textarea
                            value={JSON.stringify(action.headers || {}, null, 2)}
                            onChange={(e) => {
                                try {
                                    onChange({ ...action, headers: JSON.parse(e.target.value) });
                                } catch (err) {}
                            }}
                            rows={3}
                        />
                    </div>
                    <div className="form-group">
                        <label>Body (JSON):</label>
                        <textarea
                            value={JSON.stringify(action.body || {}, null, 2)}
                            onChange={(e) => {
                                try {
                                    onChange({ ...action, body: JSON.parse(e.target.value) });
                                } catch (err) {}
                            }}
                            rows={4}
                        />
                    </div>
                </>
            )}

            {action.type === 'switch_to_new_window' && (
                <div className="form-group">
                    <label>Timeout (seconds):</label>
                    <input
                        type="number"
                        value={action.timeout || 5}
                        onChange={(e) => onChange({ ...action, timeout: parseInt(e.target.value) })}
                        min={0}
                    />
                    <p style={{ fontSize: '12px', color: '#666', marginTop: 5 }}>
                        Use -1 to switch back to main page.
                    </p>
                </div>
            )}

            {action.type === 'switch_to_frame' && (
                <div className="form-group">
                    <label>Frame Index:</label>
                    <input
                        type="number"
                        value={action.frame_index ?? 0}
                        onChange={(e) => onChange({ ...action, frame_index: parseInt(e.target.value) })}
                        min={-1}
                    />
                    <p style={{ fontSize: '12px', color: '#666', marginTop: 5 }}>
                        Use -1 to switch back to main page.
                    </p>
                </div>
            )}
        </div>
    );
};

export default NestedActionsEditor;
